// Server fee-quote contract (SPEC-SOR-424) — the DB-free module the rest of the
// plan hangs on: the `FeeFacts` record a server-composed quote depends on, the
// deterministic digest over exactly that record, the `ServerQuoteRecord` a
// successful composition persists, the three-way `ServerQuoteSlot` a reader
// consults to learn an order's quote state, and the closed `QUOTE_VERDICTS`
// submit-time value space together with the two refusal-code vocabularies its
// refusable subset answers with (`QUOTE_REFUSAL_CODES`, `PAYMENT_REFUSAL_CODES`).
//
// Purity and carriage, same discipline as ./fee-waiver.ts and
// ./fee-waiver-basis.ts: no clock (`new Date()` with an argument only, never
// argless), no I/O, no DB, no `node:crypto` — this module rides the package's
// root barrel, which the portal bundles, so the digest is pure JS and SYNC
// (`crypto.subtle.digest` would be async and would break the property shard's
// synchronous tether).
//
// One builder, one digest. `resolveFeeFactsForOrder` is the repo's ONLY
// constructor of a `FeeFacts` value and `feeFactsHash` its ONLY digest, so a
// reader can never compose facts from a different projection than the one the
// hash pins. The facts are read from the SAME persisted snapshot a quote is
// composed from — the order row's `type_data`, the account row and the pricing
// rows — never from a caller-supplied payload, which is why the record's
// `factsOrigin` has exactly one writable value.
//
// Reuse, never re-derive: the per-party exemption facts project through the
// shared predicates in ./fee-waiver.js (`effectiveFeeExemption`,
// `partyRequestsFeeWaiver`, `isFeeWaiverRequestDoc`), and the persisted snapshot
// itself is unwrapped by the shared reader in ./order-wizard-state.js — the SAME
// one the api-gateway services and the reconciler read orders through. A second
// unwrap branch here would be exactly the drift this module exists to close: the
// quote's facts and the submit envelope must describe ONE snapshot (R15).
import { z } from 'zod/v4';
import { effectiveFeeExemption, isFeeWaiverRequestDoc, partyRequestsFeeWaiver, } from './fee-waiver.js';
import { readWizardStateFromTypeData } from './order-wizard-state.js';
// ---------------------------------------------------------------------------
// FeeFacts — exactly the declared fact set
// ---------------------------------------------------------------------------
/**
 * One party's effective fee-exemption facts. The RAW `feeExemptionType` /
 * `requestFeesExemption` pair is deliberately NOT carried: it is projected
 * through the shared predicates into the RESOLVED pair below, so the two
 * spellings of one request — `{ feeExemptionType: 'NONE', requestFeesExemption:
 * true }` and `{ feeExemptionType: 'FEE_WAIVER' }` — are ONE facts value. The
 * hash must move when the fee can move, not when a writer picks a synonym.
 */
export const FeeFactPartySchema = z.object({
    /** `effectiveFeeExemption` — the exemption actually requested. */
    effectiveFeeExemption: z.string(),
    /** `partyRequestsFeeWaiver` — the resolved waiver request (never the raw flag). */
    requestFeesExemption: z.boolean(),
    /** The existing-waiver carve-out the filer asserted. */
    feeWaiverOnFile: z.boolean(),
    /**
     * Whether the FILING connects a fee-waiver request document. Filing-level by
     * construction — one connected FW-001 satisfies every fee-waiver-requesting
     * party (`feeWaiverSatisfied`) — mirrored onto each party's projection
     * because the facts are enumerated per party.
     */
    fw001Connected: z.boolean(),
});
/**
 * One custom-pricing row in force. Carries the row's identity, its window and a
 * canonical serialization of its pricing content, because the bias here is
 * deliberately BROAD: a spurious hash change costs one recalculation, while a
 * missed one is the `stale-facts` defect itself.
 */
export const FeeFactPricingRowSchema = z.object({
    id: z.string(),
    effectiveDate: z.string(),
    /** `''` when the window is open-ended — the ONE canonical absent spelling. */
    expirationDate: z.string(),
    priority: z.number(),
    stackable: z.boolean(),
    applyToAllProducts: z.boolean(),
    /**
     * `custom_pricing.status`. Carried, not assumed: the engine applies a master
     * only while `status === 'active'`, so a disabled row and an active one are
     * different facts about the pricing in force. `''` is the canonical absent
     * spelling for a row shape that predates the column.
     */
    status: z.string(),
    /**
     * The row's entries, JSON-serialized as supplied. Entry key order is the
     * caller's, so a row re-serialized with a different key order re-quotes —
     * again the cheap direction.
     */
    entries: z.string(),
});
/**
 * The declared fee-facts record: county, case category and type code, the
 * document list, each party's effective fee-exemption facts, the account's
 * fee-waiver category, the custom pricing in force, the order's source and the
 * pricing effective date — and NOTHING else. The spec's `FeeFacts` enumerates
 * exactly this set and `feeFactsHash` is the digest of exactly this record.
 */
export const FeeFactsSchema = z.object({
    /** `orderInfo.county`, RAW: the hash must move when the value moves. */
    county: z.string(),
    caseCategoryCode: z.string(),
    caseTypeCode: z.string(),
    /**
     * The document type codes, SORTED: a reordered roster is the same facts, so a
     * query-order change must not read as a change that re-quotes.
     */
    documentTypeCodes: z.array(z.string()),
    /** Pins multiplicity, which the sorted code list alone leaves ambiguous. */
    documentCount: z.number().int().nonnegative(),
    parties: z.array(FeeFactPartySchema),
    /**
     * The account's fee-waiver category, RAW. Deliberately not the
     * `isFeeWaiverCategory` boolean: the declared fact is the CATEGORY, and
     * collapsing the non-waiver categories onto one boolean would report no
     * change where the account's pricing basis did change.
     */
    accountFeeWaiverCategory: z.string(),
    /**
     * The custom pricing in force. The caller supplies the candidates; each row's
     * own `status` and window are carried so a flip in either moves the digest
     * rather than silently changing the applied pricing (see `PricingRowLike`).
     */
    customPricing: z.array(FeeFactPricingRowSchema),
    source: z.string(),
    /**
     * The pricing window the supplied rows put in force: the newest
     * `effectiveDate` among them, by the same canonical ordering the rows are
     * projected in, and `''` (the canonical absent marker) when none is supplied.
     * Clock-free and input-derivable — the caller supplies the rows in force.
     */
    pricingEffectiveDate: z.string(),
});
// ---------------------------------------------------------------------------
// The one builder
// ---------------------------------------------------------------------------
/**
 * Canonical instant spelling for a value that may be a `Date` (a drizzle row) or
 * an ISO string (the API shape): `''` for absent, else the value's own ISO form.
 * Clock-free — an explicit `Date` is rendered, never created.
 */
function isoInstant(value) {
    if (value === null || value === undefined)
        return '';
    return value instanceof Date ? value.toISOString() : value;
}
/**
 * Project the supplied pricing rows canonically: one entry per row, ordered by
 * (window start, row id) so the query's row order is not a fact, and with the
 * row's own entry order preserved inside its serialized content.
 */
function projectPricingRows(rows) {
    return rows
        .map((row) => ({
        id: row.id,
        effectiveDate: isoInstant(row.effectiveDate),
        expirationDate: isoInstant(row.expirationDate),
        priority: row.priority ?? 0,
        stackable: row.stackable === true,
        applyToAllProducts: row.applyToAllProducts === true,
        status: row.status ?? '',
        entries: JSON.stringify(row.entries ?? []),
    }))
        .sort((a, b) => {
        if (a.effectiveDate !== b.effectiveDate)
            return a.effectiveDate < b.effectiveDate ? -1 : 1;
        if (a.id === b.id)
            return 0;
        return a.id < b.id ? -1 : 1;
    });
}
/**
 * Resolve the fee facts of ONE persisted order snapshot. Pure and total, with a
 * three-argument signature — the order row, the account row and the pricing
 * rows, and nothing else — because a fourth parameter (or an injected clock)
 * would let a caller compose facts the persisted row does not carry, which is
 * precisely the `request_payload` origin the record declares illegal.
 *
 * The facts are a PROJECTION of that snapshot, never a copy of it: only the
 * declared fields are read, and the per-party exemption pair is resolved
 * through the shared predicates so this projection can never disagree with the
 * envelope builder or the charge seam about who requests a fee waiver.
 *
 * `pricingRows` is the caller's obligation: supply the candidates whose window
 * and `status` describe the pricing the order would be priced under — in
 * practice every `custom_pricing` master for the account, since each row's own
 * window and `status` are carried into the digest and a row merely OMITTED
 * here is a row the hash cannot see. (The engine's `customPricingMastersToRules`
 * adapter applies the `status === 'active'` + window filter itself; this builder
 * deliberately does not, because filtering here would drop the disabled/expired
 * rows whose presence is itself a fact about the table in force.)
 */
export function resolveFeeFactsForOrder(orderRow, account, pricingRows) {
    const state = readWizardStateFromTypeData(orderRow.typeData);
    const documents = state?.documents ?? [];
    const parties = state?.parties ?? [];
    // Resolved ONCE for the filing: one connected FW-001 satisfies every
    // fee-waiver-requesting party, so mirroring it per party is a projection of
    // one filing-level fact, not per-party evidence.
    const fw001Connected = documents.some((doc) => isFeeWaiverRequestDoc(doc.documentType));
    const customPricing = projectPricingRows(pricingRows);
    return {
        county: state?.orderInfo?.county ?? '',
        caseCategoryCode: state?.caseInfo?.caseCategoryCode ?? '',
        caseTypeCode: state?.caseInfo?.caseType?.code ?? '',
        documentTypeCodes: documents.map((doc) => doc.documentType?.code ?? '').sort(),
        documentCount: documents.length,
        parties: parties.map((party) => ({
            effectiveFeeExemption: effectiveFeeExemption(party),
            requestFeesExemption: partyRequestsFeeWaiver(party),
            feeWaiverOnFile: party.feeWaiverOnFile === true,
            fw001Connected,
        })),
        accountFeeWaiverCategory: account.customerCategory ?? '',
        customPricing,
        source: orderRow.source,
        pricingEffectiveDate: customPricing.length === 0 ? '' : customPricing[customPricing.length - 1].effectiveDate,
    };
}
// ---------------------------------------------------------------------------
// The one digest
// ---------------------------------------------------------------------------
const FNV1A64_OFFSET_BASIS = 0xcbf29ce484222325n;
const FNV1A64_PRIME = 0x100000001b3n;
const UINT64_MASK = 0xffffffffffffffffn;
/** FNV-1a, 64-bit, over UTF-16 code units — pure JS, sync, deterministic. */
function fnv1a64(input) {
    let hash = FNV1A64_OFFSET_BASIS;
    for (let i = 0; i < input.length; i++) {
        hash = ((hash ^ BigInt(input.charCodeAt(i))) * FNV1A64_PRIME) & UINT64_MASK;
    }
    return hash.toString(16).padStart(16, '0');
}
/**
 * The deterministic digest of a `FeeFacts` record: the SHA-1-shaped contract
 * (same facts in, same digest out; any declared fact different, different
 * digest) without a crypto dependency the browser bundle cannot carry.
 *
 * The canonical string is HAND-BUILT with a fixed key order rather than
 * `JSON.stringify(facts)`: a caller-assembled object serializes in insertion
 * order, so equal facts could otherwise hash differently. This digest gates
 * re-quoting; it is not a security boundary and makes no collision-resistance
 * claim — a collision would read as a missed change, which is why the width is
 * 64 bits rather than 32.
 */
export function feeFactsHash(facts) {
    const canonical = JSON.stringify({
        county: facts.county,
        caseCategoryCode: facts.caseCategoryCode,
        caseTypeCode: facts.caseTypeCode,
        documentCount: facts.documentCount,
        documentTypeCodes: facts.documentTypeCodes.map((code) => code),
        parties: facts.parties.map((party) => ({
            effectiveFeeExemption: party.effectiveFeeExemption,
            requestFeesExemption: party.requestFeesExemption,
            feeWaiverOnFile: party.feeWaiverOnFile,
            fw001Connected: party.fw001Connected,
        })),
        accountFeeWaiverCategory: facts.accountFeeWaiverCategory,
        customPricing: facts.customPricing.map((row) => ({
            id: row.id,
            effectiveDate: row.effectiveDate,
            expirationDate: row.expirationDate,
            priority: row.priority,
            stackable: row.stackable,
            applyToAllProducts: row.applyToAllProducts,
            status: row.status,
            entries: row.entries,
        })),
        source: facts.source,
        pricingEffectiveDate: facts.pricingEffectiveDate,
    });
    return fnv1a64(canonical);
}
// ---------------------------------------------------------------------------
// ServerQuoteRecord — the persisted composition
// ---------------------------------------------------------------------------
/**
 * The record's vendor-response identity: the vendor fee block plus the waiver
 * witness. Typed against its interface for the same reason the record schema
 * is: the line-item shape is `FeeBreakdown`'s, so a field added there fails
 * `tsc` HERE rather than silently diverging from the vendor block this record
 * claims to carry.
 */
export const ServerQuoteVendorIdentitySchema = z.object({
    totalAmount: z.number(),
    lineItems: z.array(z.object({
        description: z.string(),
        amount: z.number(),
        /** COURT = court fee, JOURNAL = transaction fee (the vendor's own split). */
        category: z.enum(['COURT', 'JOURNAL']),
    })),
    /**
     * Whether the composition zeroed the EFM, LDS-service and processing legs
     * under a satisfied fee waiver — a $0 waiver quote is policy, not an
     * unpriced one, and the reader must be able to tell the two apart.
     */
    waiverZeroed: z.boolean(),
});
/**
 * The closed `factsOrigin` value space. `order_row` is the ONLY writable member:
 * a quote is composed from the order's persisted fee-facts row, so a record
 * naming any other origin — the domain's illegal `request_payload`, a
 * caller-supplied body — is unrepresentable rather than merely discouraged.
 */
export const ServerQuoteFactsOriginSchema = z.enum(['order_row']);
export const ServerQuoteRecordSchema = z.object({
    // Plain numbers, NOT integers: `ResolvedFeeQuote`'s five figures are
    // fractional DOLLARS, not integer cents. `computeProcessingFee` computes
    // `base * (rate / 100)` with no rounding ("Pure: no rounding"), `efm` is a
    // per-filing charge like 3.5, and `feeAmountText` renders the figure directly
    // as USD — so the repo's canonical quote is `{ court: 435, efm: 3.5,
    // ldsService: 60, processingFee: 18.4, total: 516.9 }`. These match the
    // existing wire contract `EffectiveFeeQuoteResponseSchema`
    // (apps/api-gateway/src/features/efiling/routes.ts), which is also plain
    // `z.number()`. An `.int()` here would reject essentially every real quote at
    // parse time, and the failed composition would funnel into the `degraded`
    // marker — a good quote persisted as a degradation, blocking submit. `tsc`
    // cannot catch it: a runtime refinement over a plain `number` field.
    court: z.number(),
    efm: z.number(),
    ldsService: z.number(),
    processingFee: z.number(),
    total: z.number(),
    factsHash: z.string().min(1),
    factsOrigin: ServerQuoteFactsOriginSchema,
    vendor: ServerQuoteVendorIdentitySchema,
    pricingEffectiveDate: z.string(),
    composedAt: z.string().min(1),
    expiresAt: z.string().min(1),
});
// ---------------------------------------------------------------------------
// ServerQuoteSlot — the one shape a reader consults
// ---------------------------------------------------------------------------
/**
 * An order's quote state, three-valued and exhaustive. This union is the ONLY
 * shape from which a reader learns whether an order has a current quote:
 *
 *  - `quoted`   — carries the record, which IS the order's current ServerQuote.
 *  - `degraded` — carries the instant a composition produced NO figures. A
 *                 server-owned degradation MARKER holding no figures and no
 *                 facts hash, and never a ServerQuote: an order whose slot
 *                 reads `degraded` carries NO current quote, while the
 *                 degradation itself stays separately representable — which is
 *                 why the later submit verdict can tell it apart from an order
 *                 that was never quoted.
 *  - `none`     — never quoted. Absence of rows reads as this; no `none` row is
 *                 ever stored.
 *
 * `not_applicable` is deliberately NOT a member: it is a submit-time VERDICT
 * (see `QUOTE_VERDICTS`), not a stored state, so folding it in here would make
 * "the gate never addresses this order" indistinguishable from "this order's
 * quote is absent".
 *
 * The `degraded` member is STRICT where `quoted` is not: a marker naming any
 * other key — the `record` a reader might try to smuggle onto a failed
 * composition — is REFUSED rather than silently stripped, so "a degradation is
 * never a ServerQuote" is a parse refusal and not a reader's memory. The
 * record-bearing member keeps the tolerant default, where extra row metadata
 * riding along changes nothing about the quote.
 */
export const ServerQuoteSlotSchema = z.discriminatedUnion('state', [
    z.object({ state: z.literal('quoted'), record: ServerQuoteRecordSchema }),
    z.object({ state: z.literal('degraded'), degradedAt: z.string().min(1) }).strict(),
    z.object({ state: z.literal('none') }),
]);
// ---------------------------------------------------------------------------
// The closed verdict value space
// ---------------------------------------------------------------------------
/**
 * The submit-time quote verdicts, total over the domain: exactly one applies to
 * every order the quote gate addresses, and the value space carries one member
 * outside that set.
 *
 *  - `current`        — the order's quote record matches its facts and is unexpired.
 *  - `missing`        — no current quote and no degraded marker.
 *  - `stale-facts`    — a record exists but the facts it carries are not the order's.
 *  - `expired`        — a record exists, matches its facts, and is past its TTL.
 *  - `degraded`       — a composition failed, so the order carries no current quote.
 *  - `not_applicable` — the order the quote gate never addresses (a non-e-filing
 *                       order), so the classification stays total without any
 *                       matrix-priced order ever taking a quote verdict.
 *
 * NOT the same value space as `QUOTE_STATES` (../types.js) — that is the
 * client-side fee-calc OUTCOME vocabulary (`resolved`/`unresolved`/`degraded`/
 * `failed`/`absent`) the wizard's Review Fees step renders. Same word "quote",
 * disjoint members, different decider.
 */
export const QUOTE_VERDICTS = [
    'current',
    'missing',
    'stale-facts',
    'expired',
    'degraded',
    'not_applicable',
];
// ---------------------------------------------------------------------------
// The refusal-code vocabularies
// ---------------------------------------------------------------------------
/**
 * The refusable verdicts' stable, machine-readable refusal codes: one code per
 * refusable `QuoteVerdict` — the four verdicts
 * `Exclude<QuoteVerdict, 'current' | 'not_applicable'>` names, which is exactly
 * the set the server's refusal faces are keyed on. `current` answers nothing
 * because the submit proceeds, and `not_applicable` because the quote gate
 * never addresses that order.
 *
 * A closed wire vocabulary, so it lives here beside the verdicts it is the
 * refusable subset of, and both apps READ it rather than keeping a copy: a code
 * added or renamed is covered by the api-gateway's `quoteRefusal` (which
 * composes the faces) and by the portal's `classifySubmitRefusal` (which routes
 * a refused submit) the moment it exists.
 */
export const QUOTE_REFUSAL_CODES = [
    'quote_missing',
    'quote_stale_facts',
    'quote_expired',
    'quote_degraded',
];
/**
 * The payment refusal's stable, machine-readable code. Deliberately a value
 * space of its own: it is disjoint from every member of `QUOTE_REFUSAL_CODES` by
 * construction, which is what keeps the payment class and the quote class from
 * ever collapsing onto one another (AX23) — the two deciders also split on
 * status (402 against 409), so neither half of that conjunction carries the
 * distinction alone.
 */
export const PAYMENT_REFUSAL_CODES = ['payment_method_required'];
